/**
 * QrCode — a QR symbol drawn as one inline SVG path (OPEN-035).
 *
 * Built from `qrcode`'s module matrix rather than `toDataURL`, so it stays crisp at any size
 * and nothing is rasterised or written anywhere: the value lives only in this element.
 * Always dark modules on a white field with the standard 4-module quiet zone, whatever the
 * page theme — a camera reading a dark-on-light code off a dark UI is the reliable case.
 */

import * as React from 'react';
import * as QRCode from 'qrcode';

/** Quiet zone in modules on each side; 4 is the QR spec minimum. */
const QUIET_MODULES = 4;

export interface QrCodeProps {
  /** The text the code encodes. */
  readonly value: string;
  /** Rendered width and height in CSS pixels. */
  readonly size: number;
  /** Accessible name for the image. */
  readonly label: string;
  readonly errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H';
}

/** The SVG path for the dark modules, offset by the quiet zone, plus the full side length. */
export function qrPath(
  value: string,
  errorCorrectionLevel: 'L' | 'M' | 'Q' | 'H' = 'M',
): { readonly d: string; readonly side: number } {
  const { modules } = QRCode.create(value, { errorCorrectionLevel });
  const { size, data } = modules;
  const parts: string[] = [];
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      if (data[y * size + x]) {
        parts.push(`M${String(x + QUIET_MODULES)} ${String(y + QUIET_MODULES)}h1v1h-1z`);
      }
    }
  }
  return { d: parts.join(''), side: size + QUIET_MODULES * 2 };
}

export function QrCode({
  value,
  size,
  label,
  errorCorrectionLevel = 'M',
}: QrCodeProps): React.JSX.Element {
  const { d, side } = React.useMemo(
    () => qrPath(value, errorCorrectionLevel),
    [value, errorCorrectionLevel],
  );
  return (
    <svg
      role="img"
      aria-label={label}
      width={size}
      height={size}
      viewBox={`0 0 ${String(side)} ${String(side)}`}
      shapeRendering="crispEdges"
      style={{ display: 'block' }}
    >
      <rect width={side} height={side} fill="#ffffff" />
      <path d={d} fill="#000000" />
    </svg>
  );
}
